/**
 * Strategy Card Component
 * 
 * Displays strategy hierarchy calculated from objectives, showing parent artifacts,
 * status rollup, and dependencies.
 */

import React, { useState, useEffect } from 'react';
import { Card, Tree, Tag, Space, Spin, Alert, Typography, List, Progress } from 'antd';
import { 
  BranchesOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  PlayCircleOutlined,
  LinkOutlined
} from '@ant-design/icons';
import { CardSectionProps } from '../../../types/compositeCards';
import { apiClient } from '../../../services/apiClient';
import styles from './StrategyCard.module.css';

const { Text, Title } = Typography;

interface StrategyArtifact {
  artifact_id: string;
  artifact_name: string;
  artifact_description?: string;
  artifact_type?: string;
  parent_artifacts: Array<{ id: string; name: string }>;
  child_artifacts: Array<{ id: string; name: string }>;
  artifact_items: Array<{
    id: string;
    name: string;
    item_type: string;
    is_improvement: boolean;
    is_validated: boolean;
  }>;
  rollup_status: {
    status: string;
    completion: number;
    completed_children: number;
    total_children: number;
  };
  needs_work: boolean;
}

interface StrategyData {
  objective: {
    id: string;
    name: string;
    description: string;
  };
  strategy_hierarchy: StrategyArtifact[];
  parent_artifact_items: Array<{
    artifact_id: string;
    artifact_name: string;
    item_id: string;
    item_name: string;
    status: string;
  }>;
  dependencies: Array<{
    from_artifact_id: string;
    to_artifact_id: string;
    dependency_type: string;
  }>;
  total_steps: number;
  completed_steps: number;
}

export const StrategyCard: React.FC<CardSectionProps> = ({ 
  card,
  onAction
}) => {
  const { data } = card;
  const [strategyData, setStrategyData] = useState<StrategyData | null>(
    'strategy' in data ? (data.strategy as StrategyData) : null
  );
  const [loading, setLoading] = useState(!strategyData);
  const [error, setError] = useState<string | null>(null);

  const objectiveId = 'objectiveId' in data ? data.objectiveId : null;

  useEffect(() => {
    if (!strategyData && objectiveId) {
      void loadStrategy();
    }
  }, [objectiveId]);

  const loadStrategy = async () => {
    if (!objectiveId) {
      setError('Objective ID required');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get(`/objectives/${objectiveId}/strategy`);
      setStrategyData(response.data);
    } catch (err: any) {
      console.error('Failed to load strategy:', err);
      setError(err.response?.data?.detail || 'Failed to load strategy');
    } finally {
      setLoading(false);
    }
  };

  const getStatusTag = (status: string) => {
    switch (status) {
      case 'completed':
        return <Tag color="green" icon={<CheckCircleOutlined />}>Completed</Tag>;
      case 'in_progress':
        return <Tag color="blue" icon={<PlayCircleOutlined />}>In Progress</Tag>;
      case 'not_started':
        return <Tag color="default" icon={<ClockCircleOutlined />}>Not Started</Tag>;
      default:
        return <Tag>{status}</Tag>;
    }
  };

  const buildTreeData = (artifacts: StrategyArtifact[]): any[] => {
    // Find root artifacts (those with no parents in the list)
    const artifactMap = new Map(artifacts.map(a => [a.artifact_id, a]));
    const rootArtifacts = artifacts.filter(a => 
      !a.parent_artifacts.some(p => artifactMap.has(p.id))
    );

    const buildNode = (artifact: StrategyArtifact): any => {
      const children = artifacts
        .filter(a => a.parent_artifacts.some(p => p.id === artifact.artifact_id))
        .map(buildNode);

      return {
        title: (
          <Space>
            <Text strong>{artifact.artifact_name}</Text>
            {getStatusTag(artifact.rollup_status.status)}
            {artifact.needs_work && <Tag color="orange">Needs Work</Tag>}
          </Space>
        ),
        key: artifact.artifact_id,
        children: children.length > 0 ? children : undefined,
        isLeaf: children.length === 0
      };
    };

    return rootArtifacts.map(buildNode);
  };

  if (loading) {
    return (
      <Card className={styles.strategyCard}>
        <Spin tip="Loading strategy..." />
      </Card>
    );
  }

  if (error) {
    return (
      <Card className={styles.strategyCard}>
        <Alert message="Error" description={error} type="error" showIcon />
      </Card>
    );
  }

  if (!strategyData) {
    return (
      <Card className={styles.strategyCard}>
        <Alert message="No strategy data" type="info" />
      </Card>
    );
  }

  const { objective, strategy_hierarchy, parent_artifact_items, total_steps, completed_steps } = strategyData;
  const treeData = buildTreeData(strategy_hierarchy);

  return (
    <Card 
      className="strategy-card"
      title={
        <Space>
          <BranchesOutlined />
          <span>Strategy: {objective.name}</span>
        </Space>
      }
    >
      {/* Progress Summary */}
      <div style={{ marginBottom: 16 }}>
        <Title level={5}>Strategy Progress</Title>
        <Progress 
          percent={total_steps > 0 ? (completed_steps / total_steps * 100) : 0}
          format={() => `${completed_steps} of ${total_steps} steps completed`}
        />
      </div>

      {/* Strategy Hierarchy */}
      {strategy_hierarchy.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <Title level={5}>Artifact Hierarchy</Title>
          <Tree
            treeData={treeData}
            defaultExpandAll={false}
            showIcon={false}
          />
        </div>
      )}

      {/* Parent Artifact Items (High-Level Steps) */}
      {parent_artifact_items.length > 0 && (
        <div>
          <Title level={5}>High-Level Steps</Title>
          <List
            dataSource={parent_artifact_items}
            renderItem={(item) => (
              <List.Item>
                <Space>
                  {item.status === 'completed' ? (
                    <CheckCircleOutlined style={{ color: '#52c41a' }} />
                  ) : (
                    <ClockCircleOutlined style={{ color: '#1890ff' }} />
                  )}
                  <div>
                    <Text strong>{item.artifact_name}</Text>
                    <div>
                      <Text type="secondary" style={{ fontSize: 12 }}>{item.item_name}</Text>
                    </div>
                  </div>
                  {getStatusTag(item.status)}
                </Space>
              </List.Item>
            )}
          />
        </div>
      )}
    </Card>
  );
};

